import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Slider from '@mui/material/Slider';

export default function ChunkingParametersPanel({
  chunkingParamsOpen,
  setChunkingParamsOpen,
  isChunking,
  computedChunkCount,
  windowSize,
  onWindowSizeChange,
  overlap,
  onOverlapChange,
  setIsChunking
}) {
  return (
    <Box
      mb={.5}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        border: '1px solid',
        borderColor: 'grey.300',
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: 2,
        padding: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            Chunking Parameters
          </Typography>
          {!isChunking && (computedChunkCount > 0) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, fontWeight: 400, fontSize: '0.75em' }}
            >
              Chunks: <b>{computedChunkCount}</b>
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center">
          {isChunking && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
              <CircularProgress size={18} />
              <span style={{ fontSize: '0.85em' }}>Chunking...</span>
            </Box>
          )}
          <Tooltip
            title="Configure how the text is divided into overlapping chunks for processing. 'Window Size' sets the number of tokens per chunk; 'Overlap' sets how many tokens are shared between consecutive chunks."
            placement="top"
            arrow
          >
            <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'pointer', mr: 1 }} />
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => setChunkingParamsOpen((open) => !open)}
            aria-label={chunkingParamsOpen ? "Collapse" : "Expand"}
            sx={{
              transform: chunkingParamsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={chunkingParamsOpen}>
        {chunkingParamsOpen && (
          <>
            <Box>
              <Tooltip
                title="Set the number of tokens in each chunk. Larger chunk sizes capture more context per chunk."
                placement="top"
                arrow
              >
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Window Size: {windowSize}
                </Typography>
              </Tooltip>
              <Slider
                min={3}
                max={20}
                step={1}
                value={windowSize}
                marks={[...Array(20 - 3 + 1)].map((_, idx) => ({
                  value: idx + 3,
                  label: (idx + 3).toString(),
                }))}
                onChange={(_, v) => {
                  const newWindowSize = Number(v);
                  if (overlap >= newWindowSize) {
                    onOverlapChange(newWindowSize - 1);
                  }
                  onWindowSizeChange(newWindowSize);
                }}
                onMouseDown={() => setIsChunking(true)}
                onTouchStart={() => setIsChunking(true)}
                valueLabelDisplay="auto"
                sx={{
                  width: '95%',
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    mt: 0.125,
                  },
                }}
              />
            </Box>
            <Box>
              <Tooltip
                title="Set how many tokens overlap between consecutive chunks. Higher overlap means more shared context between chunks."
                placement="top"
                arrow
              >
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Overlap: {overlap}
                </Typography>
              </Tooltip>
              <Slider
                min={0}
                max={Math.max(windowSize - 1, 0)}
                step={1}
                value={overlap}
                marks={Array.from({ length: Math.max(windowSize, 1) }, (_, i) => ({
                  value: i,
                  label: i.toString(),
                }))}
                onChange={(_, v) => onOverlapChange(Number(v))}
                onMouseDown={() => setIsChunking(true)}
                onTouchStart={() => setIsChunking(true)}
                valueLabelDisplay="auto"
                sx={{
                  width: '95%',
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    mt: 0.125,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Collapse>
    </Box>
  );
}
